import { axiosInstance } from './api';

export type SetupStage =
  | 'ENROLLED'
  | 'WG_CONFIGURED'
  | 'WG_HANDSHAKE_VERIFIED'
  | 'SSH_PUBKEY_AUTHORIZED'
  | 'AUTHORIZED_KEYS_PULLED'
  | 'REVERSE_SSH_LIVE'
  | 'GITLAB_REGISTERED'
  | 'TRAEFIK_REGISTERED'
  | 'DOCKER_RUNNING';

export type HardwareModel = 'JETSON_NANO' | 'JETSON_ORIN' | 'OTHER';

export interface LocalComputer {
  id: string;
  /**
   * Operator-chosen logical key. One-E spelling for surf locations
   * (e.g. `SURF_COFFE_LEFORT`) — matches the jetson runtime + the
   * `Zone.deployment` column. Per-target spellings live in the
   * three identity-field columns below.
   */
  deployment_key: string | null;
  /**
   * GitLab Environment name + dynamic-matrix LOCATION token.
   * Two-E spelling for surf locations (e.g. `SURF_COFFEE_LEFORT`).
   * Operator-editable (task #93). Empty until set; data-migration
   * backfilled it for the 12 pre-existing bound LCs (correcting
   * the historical one-E typo for surf).
   */
  gitlab_environment_name: string | null;
  /**
   * Prometheus `DEPLOYMENT_NAME` label — lowercase + hyphen-separated
   * (e.g. `surf-coffee-lefort`). Operator-editable; data-migration
   * backfilled.
   */
  prometheus_deployment_name: string | null;
  /**
   * GitLab group-variable key for this LC's per-jetson variables
   * (e.g. `BARE_METAL_VARIABLES_SURF_COFFEE_LEFORT`). Operator-editable;
   * data-migration backfilled.
   */
  bare_metal_variable_name: string | null;
  display_name: string;
  hostname: string;
  wg_ip: string | null;
  wg_pubkey: string;
  /**
   * Jetson-generated SSH pubkey. Promoted from `machine_facts.ssh_pubkey`
   * JSONB to a top-level column in BE migration 0003 (task #82 / xxvii).
   * Empty string when not yet reported. Read-only on the FE — set only
   * via `/remote/report-ssh-pubkey/` from the jetson side.
   */
  ssh_pubkey: string;
  /**
   * Jetson's eth0 hardware MAC (lowercase `aa:bb:cc:dd:ee:ff`).
   * Identity key for the discovery `/remote/register/` endpoint
   * (task #82 / xxvii). Null until the first discovery POST
   * backfills it. Read-only on the FE.
   */
  eth0_mac: string | null;
  reverse_ssh_port: number | null;
  device_number: number | null;
  hardware_model: HardwareModel;
  setup_stage: SetupStage;
  is_archived: boolean;
  /**
   * @deprecated Removed in BE migration 0004 (task #82 / xxvii). The BE
   * no longer returns this field; reads will be `undefined` after the
   * BE deploy lands. Kept here as `string | null` (not made optional)
   * so existing callsites still typecheck — they'll just render the
   * "no heartbeat" branch until they're cleaned up in the follow-up
   * FE commit. Prometheus's node_exporter replaces per-LC heartbeat.
   */
  last_heartbeat_at: string | null;
  // ── #96: GitLab as source-of-truth for jetson app.env + ──────────
  //          BARE_METAL_VARIABLES. Fields below mirror the GitLab
  //          group-variable contents. Operator-editable until first
  //          push; read-only afterwards (Pull replaces local values
  //          with GitLab as authoritative).
  /** Comma-separated PLUGINS list ("table_control,empty_shelf,…"). */
  plugins_list: string;
  /** Comma-separated MODULES list. */
  modules_list: string;
  /** PROD | DEV | SHARED | STAGING. */
  service_mode: ServiceMode;
  /** Optional integer port; null when not set on this LC. */
  service_port: number | null;
  service_name: string;
  metal_machine_host: string;
  metal_machine_user: string;
  /** OUTBOUND SSH-proxy port — distinct from `reverse_ssh_port`
   *  (the 23NN reverse-tunnel terminator on linza-dev). */
  ssh_port: number;
  /** Write-once tracking flag. Actual secret value lives only on
   *  GitLab; BE never stores plaintext. FE shows "✓ установлен"
   *  vs an empty input. Use `setLcSecret()` to set. */
  metal_machine_password_set: boolean;
  need_sstpc_vpn: boolean;
  /** Only meaningful when `need_sstpc_vpn === true`. */
  router_domain: string;
  sstpc_user: string;
  /** Same write-once pattern as `metal_machine_password_set`. */
  sstpc_password_set: boolean;
  /** Rare flag for image rebuild — toggled via a one-off push. */
  rebuild_triton: boolean;
  /** This jetson builds the fleet's TensorRT .plan (model-conversion host).
   *  LIVE flag — flips True only when the point's convert_drainer actually polls. */
  is_model_convert_host: boolean;
  /** Operator intent (set from the FE panel) that this point should be a
   *  convert-host. Unlocks the convert installer; `is_model_convert_host`
   *  becomes the live flag once the drainer checks in. */
  convert_host_designated: boolean;
  proxy_url: string;
  /** app.env per-LC override — only LC-distinguishing app.env field
   *  the BE tracks (e.g. `telegram/surf-coffee-radio`). */
  telegram_data_save_path: string;
  /**
   * When the LC was matched-against or created in GitLab. Set on
   * either `matched` or `created` outcome of
   * `/match-or-create-on-gitlab/`. Renamed from
   * `last_pushed_to_gitlab_at` in gitlab-pipeline's #96 migration
   * (now live).
   */
  gitlab_created_at: string | null;
  /**
   * Lock signal for the Section-1 params form. Non-null = form
   * read-only (gitlab is source-of-truth for these LC fields).
   * Unlock path: `PATCH {gitlab_locked_at: null}` (requires
   * heavyweight confirmation; see UnlockGitlabDialog).
   */
  gitlab_locked_at: string | null;
  /** Snapshot of the dict pushed/pulled to/from GitLab on the last
   *  sync. Opaque to the FE — for diagnostics only. */
  gitlab_push_snapshot: Record<string, unknown> | null;

  // ── APP_ENV additions (Phase B per team-lead 2026-05-23) ─────────
  //
  // 15 runtime config fields hidden behind a "Show advanced" toggle
  // by default — they're shared-looking constants with sensible
  // defaults that 95% of operators won't edit per-LC. Defaults come
  // from the BE migration's `default=` clause; the FE renders
  // `lc.field_name` directly and the BE serializer fills in the
  // default value for fresh LCs.
  batch_size: number;
  conn_health_checks: boolean;
  debug: boolean;
  default_triton_url: string;
  enable_s3: boolean;
  frame_workload_enabled: boolean;
  gstreamer_pipeline: string;
  model_empty_shelf_name: string;
  queue_threads_num: number;
  redis_url: string;
  s3_access_key: string;
  s3_bucket: string;
  yolo_detectable_class_ids: string;
  yolo_model_name: string;
  yolo_model_version: string;

  // ── APP_ENV secrets ─────────────────────────────────────────────
  /** Django SECRET_KEY for the LC's local containers. Write-once via
   *  /set-secret/?name=django_salt. */
  django_salt_set: boolean;
  /** AWS secret access key (paired with the plaintext access_key
   *  above). Write-once. */
  s3_secret_access_key_set: boolean;
  /** Sentry DSN — plaintext URL but carries an embedded secret. */
  sentry_dsn_set: boolean;
  /** Telegram bot token for alerting from the LC. */
  telegram_alert_token_set: boolean;

  // ── DB_ENV secret (DB host/port/etc are shared template
  //     constants; only the password is per-LC) ───────────────────
  postgres_password_set: boolean;

  machine_facts?: Record<string, string>;
  notes: string;
  created_at: string;
  updated_at: string;
}

/** Enum for `LocalComputer.service_mode`. Locked to the four values
 *  gitlab-pipeline confirmed via the existing Pi vars; expandable
 *  later if more values appear in the wild. */
export type ServiceMode = 'PROD' | 'DEV' | 'SHARED' | 'STAGING';
export const SERVICE_MODES: ServiceMode[] = ['PROD', 'DEV', 'SHARED', 'STAGING'];

export interface LocalComputersListResponse {
  count: number;
  limit: number;
  offset: number;
  results: LocalComputer[];
}

export const SETUP_STAGES_ORDERED: SetupStage[] = [
  'ENROLLED',
  'WG_CONFIGURED',
  'WG_HANDSHAKE_VERIFIED',
  'SSH_PUBKEY_AUTHORIZED',
  'AUTHORIZED_KEYS_PULLED',
  'REVERSE_SSH_LIVE',
  'GITLAB_REGISTERED',
  'TRAEFIK_REGISTERED',
  'DOCKER_RUNNING',
];

const BASE = '/local_computers/admin/';

export const listLocalComputers = (params: { q?: string; limit?: number; offset?: number } = {}) => {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.limit != null) search.set('limit', String(params.limit));
  if (params.offset != null) search.set('offset', String(params.offset));
  const qs = search.toString();
  const url = qs ? `${BASE}?${qs}` : BASE;
  return axiosInstance.get<LocalComputersListResponse>(url);
};

export const getLocalComputer = (id: string) =>
  axiosInstance.get<LocalComputer>(`${BASE}${id}/`);

export type LocalComputerPatch = Partial<{
  deployment_key: string | null;
  display_name: string;
  is_archived: boolean;
  notes: string;
  hardware_model: HardwareModel;
  wg_ip: string | null;
  wg_pubkey: string;
  reverse_ssh_port: number | null;
  device_number: number | null;
  setup_stage: SetupStage;
}>;

export const patchLocalComputer = (id: string, payload: LocalComputerPatch) =>
  axiosInstance.patch<LocalComputer>(`${BASE}${id}/`, payload);

export interface IssueTokenRequest {
  wg_ip?: string;
  device_number?: number;
  display_name?: string;
  hardware_model?: HardwareModel;
}

export interface IssueTokenResponse {
  local_computer: LocalComputer;
  token: string;
  expires_at: string;
  /** Legacy inline-JSON POST to /remote/checkin — kept for debugging only. */
  checkin_curl: string;
  /** `curl ... /setup-script/<token>/ | bash` — the Brick-2 entry point. */
  setup_curl: string;
}

export const issueEnrollmentToken = (payload: IssueTokenRequest) =>
  axiosInstance.post<IssueTokenResponse>(`${BASE}issue-token/`, payload);

/**
 * Drop any existing enrollment token on this row and mint a fresh 24h one.
 * Same response shape as `issueEnrollmentToken` — used by the detail modal
 * when an unbound row is reopened without an active curl in hand.
 */
export const reissueEnrollmentToken = (id: string) =>
  axiosInstance.post<IssueTokenResponse>(`${BASE}${id}/reissue-token/`);

export const deleteLocalComputer = (id: string) =>
  axiosInstance.delete<void>(`${BASE}${id}/`);

/**
 * `InfraSnippetsResponse` + `getInfraSnippets` removed in #90 cutover.
 * The GitLab CI matrix is now BE-driven (`/admin/deploy-matrix.yml`
 * + dynamic child pipeline), Traefik jetson routing was reverted in
 * #69, and the SetupCard's ManualStageWaiting body covers the
 * "what to do next" copy without a snippet panel. The BE endpoint
 * stays alive for now (per gitlab-pipeline's coordination message
 * 2026-05-23) but the FE no longer calls it.
 */

/**
 * Brick-5 node_exporter probe — opens a real TCP connection from the
 * celery container to wg_ip:9100/metrics over WireGuard. Returns up/down
 * with a snippet of the metrics body for sanity.
 */
export interface NodeExporterProbeResponse {
  up: boolean;
  url?: string;
  reason?: string;
  first_chars?: string;
}

export const probeNodeExporter = (id: string) =>
  axiosInstance.get<NodeExporterProbeResponse>(`${BASE}${id}/node-exporter-probe/`);

// ── #96 GitLab source-of-truth endpoints ─────────────────────────

/** One conflict row from `gather_clashes` on the BE. Each carries
 *  the offending field name + a reason discriminator + per-reason
 *  extra fields (existing_lc_id / existing_env_name / etc.). */
export interface GitlabClash {
  field: string;
  reason:
    | 'duplicate_in_local_computers_table'
    | 'environment_already_exists_in_gitlab'
    | 'group_variable_already_exists_in_gitlab'
    | string; // forward-compat: BE may add reason variants
  /** When duplicate_in_local_computers_table: the LC that already owns this value. */
  existing_lc_id?: string;
  existing_lc_display_name?: string;
  /** When environment_already_exists_in_gitlab. */
  existing_env_id?: number;
  existing_env_name?: string;
  /** When group_variable_already_exists_in_gitlab. */
  existing_var_key?: string;
}

/** Discriminated union of /match-or-create-on-gitlab/ outcomes.
 *  Discriminator: `action`. */
export type MatchOrCreateResponse =
  | {
      action: 'matched';
      environment_name: string;
      host: string;
      port: number;
      pulled_field_count: number;
      advanced_stage: boolean;
      local_computer: LocalComputer;
    }
  | {
      action: 'no_match';
      searched_host: string;
      searched_port: number;
      local_computer: LocalComputer;
    }
  | {
      action: 'created';
      environment_name: string;
      bare_metal_var_key: string;
      advanced_stage: boolean;
      local_computer: LocalComputer;
    }
  | {
      action: 'clash';
      conflicts: GitlabClash[];
      local_computer: LocalComputer;
    };

export interface MatchOrCreateError {
  detail: string;
  /** When `reason === 'lc_already_locked'`: the existing lock
   *  timestamp + env. Returned with 409. */
  reason?: 'lc_already_locked' | string;
  locked_at?: string;
  gitlab_environment_name?: string;
  /** When the call escaped to GitLab and got a 5xx back. */
  gitlab_status?: number;
}

/** Orchestrate the match-or-create flow. `force_create=false`
 *  (default) attempts to match an existing GitLab environment by
 *  metal_machine_host + ssh_port. If matched, pulls GitLab values
 *  into the LC + advances setup_stage REVERSE_SSH_LIVE →
 *  GITLAB_REGISTERED + sets gitlab_created_at + gitlab_locked_at.
 *  If no match, returns `action: "no_match"` so the operator can
 *  re-try (e.g. after editing host/port in GitLab UI) or commit
 *  to creating via a follow-up call with `force_create=true`. */
export const matchOrCreateOnGitlab = (id: string, forceCreate = false) =>
  axiosInstance.post<MatchOrCreateResponse>(
    `${BASE}${id}/match-or-create-on-gitlab/`,
    { force_create: forceCreate },
  );

export interface CheckClashesResponse {
  conflicts: GitlabClash[];
  local_computer: LocalComputer;
}

/** Pre-flight unique-field validation against the current GitLab
 *  state + the LC table. Used by the FE to surface clashes BEFORE
 *  the operator commits to a create. */
export const checkClashes = (id: string) =>
  axiosInstance.post<CheckClashesResponse>(`${BASE}${id}/check-clashes/`, {});

export interface PullFromGitlabResponse {
  action: 'pulled';
  environment_name: string;
  /** How many LC fields actually changed during this pull. 0 = LC was
   *  already in sync with GitLab. */
  pulled_field_count: number;
  /** List of LC field names that the pull mutated. Used for the
   *  success-toast detail line. */
  changed_fields: string[];
  /** Post-pull full _serialize() of the LC. */
  local_computer: LocalComputer;
}

export interface PullFromGitlabError {
  detail: string;
  /** 409 only: `lc_not_locked` when the operator hits Pull on an
   *  LC that was never matched/created. */
  reason?: 'lc_not_locked' | string;
  gitlab_locked_at?: string | null;
  gitlab_environment_name?: string;
  /** 502 only: GitLab API's HTTP status. */
  gitlab_status?: number;
}

/** Pull the GitLab-stored LC config (BARE_METAL_VARIABLES + APP_ENV
 *  + DB_ENV) back onto the LC mirror. Used when the operator edits
 *  a value via the GitLab UI directly and wants the FE to refresh.
 *  Does NOT touch `gitlab_locked_at` / `setup_stage` — pure mirror
 *  refresh. Returns the changed field list for a "what changed"
 *  success toast. */
export const pullFromGitlab = (id: string) =>
  axiosInstance.post<PullFromGitlabResponse>(`${BASE}${id}/pull-from-gitlab/`, {});

/** Unlock the LC's GitLab lock so Section-1 fields are editable
 *  again. BE accepts `PATCH {gitlab_locked_at: null}` only when the
 *  field was previously non-null; the value can only be set to
 *  null (never to a timestamp) via the FE.
 *
 *  GATE THIS BEHIND A HEAVYWEIGHT CONFIRMATION on the FE — once
 *  unlocked + edits land, the LC mirror drifts from GitLab until
 *  the next match-or-create round-trip. */
export const unlockGitlabLock = (id: string) =>
  patchLocalComputer(id, { gitlab_locked_at: null } as never);

export type LcSecretName =
  | 'metal_machine_password'
  | 'sstpc_password'
  // Phase B additions (team-lead 2026-05-23). BE accepts these
  // via the same /admin/<id>/set-secret/?name=... endpoint;
  // each one stores plaintext only on GitLab + flips the
  // matching `<name>_set` boolean on the LC.
  | 'django_salt'
  | 's3_secret_access_key'
  | 'sentry_dsn'
  | 'telegram_alert_token'
  | 'postgres_password';

export interface SetLcSecretResponse {
  ok: true;
  name: LcSecretName;
  set: true;
  set_at: string;
}

/** Set one of the write-once secrets. The plaintext goes to GitLab
 *  on the next push; locally only the `*_set` boolean flag flips.
 *  BE never stores the plaintext. */
export const setLcSecret = (id: string, name: LcSecretName, value: string) =>
  axiosInstance.post<SetLcSecretResponse>(`${BASE}${id}/set-secret/`, { name, value });

/**
 * Last failure recorded by `/remote/report-error/` on a given LC. Same
 * sub-dict shape on the full `LocalComputer.machine_facts.last_setup_error`
 * and on `LocalComputerSummary.last_setup_error` (BE commit `acf1ba6`
 * lifted the field onto the summary so the wizard's STAGE 1 card can
 * surface `<KeyMismatchBanner>` without chain-fetching the full row).
 */
export interface SetupError {
  at?: string;
  stage?: string;
  exit_code?: number | string;
  failed_cmd?: string;
}

export interface LocalComputerSummary {
  id: string;
  display_name: string;
  hostname: string;
  hardware_model: HardwareModel;
  setup_stage: SetupStage;
  is_archived: boolean;
  /**
   * Canonical jetson device number (e.g. 14 for Riders Lodge).
   * Surfaced by the BE summary endpoint as the operator-facing
   * identifier (#N) next to the branch name on the Equipment card.
   */
  device_number: number | null;
  last_heartbeat_at: string | null;
  /**
   * The LC's WireGuard mesh address (e.g. `10.99.0.21`). Surfaced in the
   * summary so callers can render live mesh state via
   * `lookupWgPeer(wg_ip)` without having to chain-fetch the full row.
   * Added by BE commit `9bfd608`.
   */
  wg_ip: string | null;
  /**
   * Latest setup-script failure (or null when none recorded). Mirrors
   * `LocalComputer.machine_facts.last_setup_error`. Surfaced by BE
   * commit `acf1ba6` so the wizard's `<KeyMismatchBanner>` heuristic
   * works against the summary endpoint.
   */
  last_setup_error: SetupError | null;
}

export const getZoneLocalComputer = (zoneId: number | string) =>
  axiosInstance.get<LocalComputerSummary>(`/local_computers/zones/${zoneId}/lc/`);

// ── LC↔Zone bind (#14) ─────────────────────────────────────────────
//
// GET returns the zones currently bound to this LC + the unbound
// candidate pool. POST flips a single zone's Zone.deployment to/from
// the LC's deployment_key.

export interface ZoneBindSummary {
  id: number;
  humanreadable_name: string;
  trassir_name: string;
  current_deployment: string;
}

export interface LcZonesResponse {
  lc_deployment_key: string;
  bound: ZoneBindSummary[];
  candidates: ZoneBindSummary[];
}

export const getLcZones = (id: string) =>
  axiosInstance.get<LcZonesResponse>(`${BASE}${id}/zones/`);

export const bindLcZone = (id: string, zoneId: number) =>
  axiosInstance.post<{ action: 'bind'; zone: ZoneBindSummary }>(
    `${BASE}${id}/zones/`,
    { action: 'bind', zone_id: zoneId },
  );

export const unbindLcZone = (id: string, zoneId: number) =>
  axiosInstance.post<{ action: 'unbind'; zone: ZoneBindSummary }>(
    `${BASE}${id}/zones/`,
    { action: 'unbind', zone_id: zoneId },
  );

/**
 * Look up a wg peer on the server by its tunnel IP.
 *
 * Returns the pubkey + bookkeeping fields read from `wg0.conf` / live wg state.
 *
 * Status codes:
 *  - 200: peer found, payload below
 *  - 404: no peer with this IP in wg0.conf
 *  - 503: wg state on the server is currently unavailable
 */
export interface WgPeerLookupResponse {
  wg_pubkey: string;
  comment: string;
  allowed_ips: string;
  /** ISO-8601 UTC, or null if wg has never seen this peer. */
  latest_handshake: string | null;
  has_handshake_recently: boolean;
}

export const lookupWgPeer = (wg_ip: string) =>
  axiosInstance.post<WgPeerLookupResponse>(`${BASE}wg-peer-lookup/`, { wg_ip });

// `resetLcKeys` + `ResetKeysRequest` removed in task #82 / xxvii: rebind
// path is now a discovery-curl rerun, not an admin action. The BE
// `/admin/<id>/reset-keys/` endpoint stays alive in this release cycle
// (defensive; lets a stale FE on prod still function) but the FE no
// longer exposes a button for it.

// ── next-free suggesters ────────────────────────────────────────────
//
// Shared by IssueTokenDialog (LocalComputersListPage) + NetworkIdentityBlock
// (LocalComputerDetailModal). Both surfaces show "auto value looking
// static, but editable on click" — per Alexey #53. The BE validates
// uniqueness on POST/PATCH, so these are advisory only.

/**
 * Pick the smallest integer in `[1, 99]` not already taken as a
 * `device_number` on any existing LC, optionally ignoring one LC by id
 * (so the detail-modal editor doesn't suggest a value that's "taken"
 * by the row being edited itself). Returns `1` if nothing is taken;
 * returns `99` if all 99 slots are exhausted.
 */
export function nextFreeDeviceNumber(
  lcs: LocalComputer[],
  ignoreLcId?: string,
): number {
  const taken = new Set(
    lcs
      .filter(l => l.id !== ignoreLcId)
      .map(l => l.device_number)
      .filter((n): n is number => typeof n === 'number'),
  );
  for (let i = 1; i <= 99; i++) if (!taken.has(i)) return i;
  return 99;
}

/**
 * Pick the smallest `10.99.0.X` (X in `[2, 254]`) not already taken.
 * `.1` is the server itself, `.0`/`.255` are network/broadcast.
 * `ignoreLcId` lets the detail-modal editor exclude the row being edited.
 *
 * **CLIENT-SIDE FALLBACK ONLY** (task #79). The authoritative
 * suggester is BE's `GET /admin/suggested-ids/`, which overlays the
 * live `/var/lib/linza/wg_peers.json` dump on top of the LC table —
 * catching non-LC peers (LinzaHQ-style office links, operator
 * laptops) that this LC-only scan can't see. Prefer
 * `fetchSuggestedIds()` below; this function stays around as a
 * last-resort fallback for the brief window when the dump cron
 * hasn't refreshed yet OR when the network request itself fails.
 */
export function nextFreeWgIp(
  lcs: LocalComputer[],
  ignoreLcId?: string,
): string {
  const taken = new Set(
    lcs
      .filter(l => l.id !== ignoreLcId)
      .map(l => l.wg_ip)
      .filter((s): s is string => typeof s === 'string' && /^10\.99\.0\.\d+$/.test(s)),
  );
  for (let i = 2; i <= 254; i++) {
    const candidate = `10.99.0.${i}`;
    if (!taken.has(candidate)) return candidate;
  }
  return '10.99.0.2';
}

/**
 * Task #79: server-side suggester. Considers BOTH the LC table AND
 * /var/lib/linza/wg_peers.json (non-LC peers). Falls back to local
 * computation if the BE call fails (network error, dump cron
 * warming up). The FE shows the values as editable so an operator
 * can override.
 */
export interface SuggestedIdsResponse {
  wg_ip: string | null;
  device_number: number | null;
  wg_dump_used: boolean;
}

export const fetchSuggestedIds = () =>
  axiosInstance.get<SuggestedIdsResponse>(`${BASE}suggested-ids/`);

// ── Discovery flow (task #82 / xxvii) ─────────────────────────────────
//
// The new register modal copies a curl one-liner that runs the
// token-less discovery script on the jetson. The script POSTs the
// jetson's eth0 MAC + already-set wg/ssh state to /remote/register/.
//
// The discovery script URL is rendered server-side; the FE only needs
// to surface the curl one-liner. The register POST itself is invoked
// from the jetson, not from the FE — but the FE typing here helps
// when polling `listLocalComputers({ q: mac })` to detect arrival.

const REMOTE_BASE = '/local_computers/remote/';

/**
 * URL the FE embeds in the copyable curl one-liner.
 *
 * Two modes (task #89):
 *   - Tokenless (no `token` arg): factory-fresh registration from the
 *     list page's Register modal. The BE matches by `eth0_mac` to
 *     find-or-create the LC.
 *   - Tokenized (`token` set): refresh-from-jetson from a specific
 *     LC's detail-page Discovery panel. The token is appended as
 *     `?token=` and travels through the script's POST payload,
 *     binding the report to that LC.
 *
 * Anchored against the configured API_BASE so dev / staging / prod
 * each get their own discovery endpoint. axiosInstance is not used
 * here because the operator copies the URL — they need the full
 * absolute string, not a relative path.
 */
export const getDiscoveryScriptUrl = (token?: string): string => {
  const base = axiosInstance.defaults.baseURL || '';
  const url = `${base}${REMOTE_BASE}discovery-script/`;
  return token ? `${url}?token=${encodeURIComponent(token)}` : url;
};

/**
 * Shape returned by `POST /local_computers/admin/issue-discovery-token/`.
 *
 * The new-LC `/remote/register/` path now requires a token (post-
 * gitlab-pipeline `7e908b1` BE deploy). The Register modal calls
 * this endpoint on open + embeds the returned `discovery_curl`
 * verbatim — no need to surface the token separately.
 *
 * Token is single-use + 24h expiry. If the operator's session
 * lingers past expiry or they retry after a successful registration,
 * the next register POST 401s and they need a fresh token via a
 * re-call.
 */
export interface IssueDiscoveryTokenResponse {
  token: string;
  expires_at: string;
  /** Ready-to-paste curl one-liner — `curl -fsSL '<url>?token=<...>' | bash`. */
  discovery_curl: string;
}

export const issueDiscoveryToken = () =>
  axiosInstance.post<IssueDiscoveryTokenResponse>(
    `${BASE}issue-discovery-token/`,
    {},
  );

/**
 * Shape returned by `POST /local_computers/remote/register/`. The
 * jetson's discovery script gets this body back; the FE doesn't call
 * register directly, but the typing is shared for tests + future
 * server-side discovery preview.
 */
export interface RegisterResponse {
  ok: boolean;
  was_already_registered: boolean;
  local_computer_id: string;
  detail_url: string;
  conflicts: Array<{
    field: string;
    why: string;
    existing_prefix: string;
    incoming_prefix: string;
  }>;
}

// ── model-deploy pull-cron (step 8) ─────────────────────────────────────────
export interface ModelCronInstallResponse {
  cron_token: string;
  mode: 'jetson' | 'host';
  what: 'deploy' | 'convert';
  is_convert_host: boolean;
  convert_host_designated: boolean;
  install_command: string;
}

/** Mint (if needed) the durable cron_token + return the copy-paste install command.
 *  `what`: 'deploy' = model-update cron (all points); 'convert' = conversion cron (convert-host). */
export const getModelCronInstall = (
  id: string,
  what: 'deploy' | 'convert' = 'deploy',
  mode: 'jetson' | 'host' = 'jetson',
) =>
  axiosInstance.post<ModelCronInstallResponse>(
    `${BASE}${id}/model-cron-install/?what=${what}&mode=${mode}`,
  );

export interface CronRun {
  id: number;
  script: string;
  ran_at: string;
  rc: number;
  summary: string;
  deployed_versions: Record<string, string>;
  log_tail: string;
  received_at: string;
}

/** Recent model-deploy cron runs for the per-point cron-status panel. */
export const getCronRuns = (id: string) =>
  axiosInstance.get<{ runs: CronRun[] }>(`${BASE}${id}/cron-runs/`);
